import { createContext, useCallback, useContext } from 'react'

import type { EmbeddingPartner, SubscriptionStepId } from '@orus.eu/dimensions'
import type { EmbeddingPartnerId } from '@orus.eu/embedding-partners'
import type { CoBranding, HelpContactDetails } from '@orus.eu/pharaoh'
import type { ParameterlessTranslationKey } from '@orus.eu/translations'

const embeddingPartnerContext = createContext<{
  setEmbeddingPartnerId: (embeddingPartnerId: string | undefined) => void
  embeddingPartnerId: string | undefined
}>({
  embeddingPartnerId: undefined,
  setEmbeddingPartnerId: () => {},
})
export const EmbeddingPartnerProvider = embeddingPartnerContext.Provider

type SetEmbeddingPartner = (embeddingPartner: EmbeddingPartner) => void

export function useSetEmbeddingPartner(): SetEmbeddingPartner {
  const setEmbeddingPartnerId = useContext(embeddingPartnerContext).setEmbeddingPartnerId
  return useCallback(
    (embeddingPartner) => {
      const embeddingPartnerId = embeddingPartner === 'none' ? undefined : embeddingPartner.id
      setEmbeddingPartnerId(embeddingPartnerId)
    },
    [setEmbeddingPartnerId],
  )
}

/**
 * Low-level access to the embedding partner context: the current id and the raw
 * setter that accepts any string id (or `undefined` to revert to the default).
 *
 * Most call sites should prefer {@link useSetEmbeddingPartner} (typed) or
 * {@link useEmbeddingPartnerConfiguration} (read). This hook exists for
 * dev/testing tools that need to walk through every partner id.
 */
export function useEmbeddingPartnerControls(): {
  embeddingPartnerId: string | undefined
  setEmbeddingPartnerId: (embeddingPartnerId: string | undefined) => void
} {
  return useContext(embeddingPartnerContext)
}

export const coBrandingFactories: Record<string, () => Promise<CoBranding>> = {
  sidecare: () => import('./co-branding/sidecare/index.ts').then((m) => m.sidecareCoBranding),
  superindep: () => import('./co-branding/superindep/index.ts').then((m) => m.superindepCoBranding),
  propulse: () => import('./co-branding/propulse/index.ts').then((m) => m.propulseCoBranding),
  jump: () => import('./co-branding/jump/index.ts').then((m) => m.jumpCoBranding),
  shine: () => import('./co-branding/shine/index.ts').then((m) => m.shineCoBranding),
  legalstart: () => import('./co-branding/legalstart/index.ts').then((m) => m.legalstartCoBranding),
  legalplace: () => import('./co-branding/legalplace/index.ts').then((m) => m.legalPlaceCoBranding),
  dougs: () => import('./co-branding/dougs/index.ts').then((m) => m.dougsCoBranding),
  previi: () => import('./co-branding/previi/index.ts').then((m) => m.previiCoBranding),
  qonto: () => import('./co-branding/qonto/index.ts').then((m) => m.qontoCoBranding),
  dealt: () => import('./co-branding/dealt/index.ts').then((m) => m.dealtCoBranding),
  resco: () => import('./co-branding/resco/index.ts').then((m) => m.rescoCoBranding),
  indy: () => import('./co-branding/indy/index.ts').then((m) => m.indyCoBranding),
  revital: () => import('./co-branding/revital/index.ts').then((m) => m.revitalCoBranding),
  phenix: () => import('./co-branding/phenix/index.ts').then((m) => m.phenixCoBranding),
  auforumassurance: () => import('./co-branding/auforumassurance/index.ts').then((m) => m.auForumAssuranceCoBranding),
  obat: () => import('./co-branding/obat/index.ts').then((m) => m.obatCoBranding),
} satisfies Partial<Record<EmbeddingPartnerId, () => Promise<CoBranding>>>

type EmbeddingPartnerConfiguration = {
  name: string
  /**
   * The URL to redirect to when the user clicks on the "Retourner à <name>" button
   * in the customer menu.
   * If not set, the button will not be displayed.
   */
  returnUrl?: string
  partnerStepSubtitles: Partial<Record<SubscriptionStepId, string>>
  helpContactDetails?: HelpContactDetails
  helpDialogTitle?: string
  helpDialogExplanation?: string
  /**
   * If set, overrides the default exclusion page title and description.
   * Should be set for partners with isManagedByOrus: false
   * as well as any partner where we explicitly share leads on exclusion (e.g: Au Forum de l'Assurance)
   */
  exclusionPageTitleKey?: ParameterlessTranslationKey
  exclusionPageDescriptionKey?: ParameterlessTranslationKey
}

export function useEmbeddingPartnerConfiguration(): EmbeddingPartnerConfiguration | undefined {
  const embeddingPartnerId = useContext(embeddingPartnerContext).embeddingPartnerId
  return embeddingPartnerId ? embedingPartnersConfigurations[embeddingPartnerId] : undefined
}

const embedingPartnersConfigurations: Record<string, EmbeddingPartnerConfiguration> = {
  sidecare: {
    name: 'Sidecare',
    returnUrl: 'https://www.sidecare.com/espace-entreprise/vos-contrats',
    partnerStepSubtitles: {
      'personal-data': 'Nous allons créer un compte sur l’espace SideCare dédié aux assurances responsabilités civiles',
    },
  },
  superindep: {
    name: 'Superindep',
    partnerStepSubtitles: {},
  },
  resco: {
    name: 'Resco',
    returnUrl: 'https://www.resco.com/',
    partnerStepSubtitles: {},
  },
  revital: {
    name: 'Revital',
    partnerStepSubtitles: {},
    exclusionPageTitleKey: 'exclusion_page_partner_title',
    exclusionPageDescriptionKey: 'exclusion_page_partner_description',
    helpContactDetails: {
      email: 'contact@revital-assurances.com',
      phone: '+33 4 22 23 61 50',
    },
  },
  phenix: {
    name: 'Phenix',
    partnerStepSubtitles: {},
    exclusionPageTitleKey: 'exclusion_page_partner_title',
    exclusionPageDescriptionKey: 'exclusion_page_partner_description',
    helpContactDetails: {
      email: 'orus@phenixassurances.fr',
      phone: '+33 1 82 28 02 00',
    },
    helpDialogTitle: 'Pour contacter Phenix',
    helpDialogExplanation:
      'Nous sommes joignables par email ou par téléphone. Nous vous recontacterons le plus vite possible.',
  },
  auforumassurance: {
    name: 'Au Forum de l’Assurance',
    partnerStepSubtitles: {},
    exclusionPageTitleKey: 'exclusion_page_partner_title',
    exclusionPageDescriptionKey: 'exclusion_page_partner_description',
  },
  obat: {
    name: 'OBAT',
    partnerStepSubtitles: {},
    exclusionPageTitleKey: 'exclusion_page_partner_title',
    exclusionPageDescriptionKey: 'exclusion_page_partner_description',
  },
} satisfies Partial<Record<EmbeddingPartnerId, EmbeddingPartnerConfiguration>>
